import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { postData } from "../../Webservice/Webservice";
import { toast } from "react-toastify";

const steps = [
  "Personal Information",
  "Business Details",
  "Business Address",
];

// Simple validation helpers
const validatePan = (pan) => {
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  return panRegex.test(pan.toUpperCase());
};

const validateGst = (gst) => {
  // Most commonly accepted GSTIN format in India
  const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
  return gstRegex.test(gst.toUpperCase());
};

function Signup() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState("");

  // Password visibility
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    gender: "",
    type: "email",
    customerType: "business",

    businessName: "",
    businessType: "",
    businessRegistrationNumber: "",
    gstNumber: "",
    panNumber: "",

    businessAddress: {
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      country: "",
      pincode: "",
    },
  });

  const [errors, setErrors] = useState({});

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      if (!formData.name.trim()) newErrors.name = "Full name is required";
      if (!formData.gender) newErrors.gender = "Gender is required";
      if (!formData.email) newErrors.email = "Email is required";
      else if (!/\S+@\S+\.\S+/.test(formData.email))
        newErrors.email = "Please enter a valid email address";

      if (!formData.password) newErrors.password = "Password is required";
      else if (formData.password.length < 8)
        newErrors.password = "Password must be at least 8 characters";

      if (!formData.confirmPassword)
        newErrors.confirmPassword = "Please confirm your password";
      else if (formData.confirmPassword !== formData.password)
        newErrors.confirmPassword = "Passwords do not match";
    }

    if (step === 1) {
      if (!formData.businessName.trim())
        newErrors.businessName = "Business name is required";
      if (!formData.businessType)
        newErrors.businessType = "Please select business type";
      if (!formData.businessRegistrationNumber.trim())
        newErrors.businessRegistrationNumber = "Registration number is required";

      // PAN Validation
      if (!formData.panNumber.trim()) {
        newErrors.panNumber = "PAN number is required";
      } else if (!validatePan(formData.panNumber)) {
        newErrors.panNumber = "Invalid PAN format (e.g., ABCDE1234F)";
      }

      // GST Validation
      if (!formData.gstNumber.trim()) {
        newErrors.gstNumber = "GST number is required";
      } else if (!validateGst(formData.gstNumber)) {
        newErrors.gstNumber = "Invalid GSTIN format (e.g., 22AAAAA0000A1Z5)";
      }
    }

    if (step === 2) {
      if (!formData.businessAddress.addressLine1.trim())
        newErrors["businessAddress.addressLine1"] = "Address line 1 is required";
      if (!formData.businessAddress.city.trim())
        newErrors["businessAddress.city"] = "City is required";
      if (!formData.businessAddress.state.trim())
        newErrors["businessAddress.state"] = "State is required";
      if (!formData.businessAddress.country.trim())
        newErrors["businessAddress.country"] = "Country is required";
      if (!formData.businessAddress.pincode.trim())
        newErrors["businessAddress.pincode"] = "Pincode is required";
      else if (!/^\d{6}$/.test(formData.businessAddress.pincode))
        newErrors["businessAddress.pincode"] = "Pincode must be 6 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    let { name, value } = e.target;

    // Auto uppercase for PAN & GST
    if (name === "panNumber" || name === "gstNumber") {
      value = value.toUpperCase();
    }

    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      if (activeStep === steps.length - 1) {
        handleFinalSubmit();
      } else {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleFinalSubmit = async () => {
    if (!validateStep(2)) return;

    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      password: formData.password,
      gender: formData.gender,
      type: formData.type,
      customerType: formData.customerType,
      businessName: formData.businessName.trim(),
      businessType: formData.businessType,
      businessRegistrationNumber: formData.businessRegistrationNumber.trim(),
      gstNumber: formData.gstNumber.trim(),
      panNumber: formData.panNumber.trim(),
      businessAddress: {
        ...formData.businessAddress,
        addressLine1: formData.businessAddress.addressLine1.trim(),
        addressLine2: formData.businessAddress.addressLine2?.trim() || "",
        city: formData.businessAddress.city.trim(),
        state: formData.businessAddress.state.trim(),
        country: formData.businessAddress.country.trim(),
        pincode: formData.businessAddress.pincode.trim(),
      },
    };

    try {
      const response = await postData("customer/register", payload);
      if (response && !response.error) {
        setShowOtp(true);
        toast.success("Registration successful. Please check your email for OTP.");
      } else {
        toast.error(response?.message || "Registration failed");
      }
    } catch (error) {
      console.error("Registration error:", error);
      toast.error("Something went wrong. Please try again.");
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP");
      return;
    }

    try {
      const response = await postData("customer/verify-otp", {
        email: formData.email,
        code: otp,
        customerType: "business",
      });

      if (response && !response.error) {
        toast.success("Account verified successfully!");
        navigate("/signin");
      } else {
        toast.error(response?.message || "Invalid OTP");
      }
    } catch (error) {
      toast.error("OTP verification failed");
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <>
            <TextField
              fullWidth
              label="Full Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              margin="normal"
              required
              error={!!errors.name}
              helperText={errors.name}
            />

            <TextField
              select
              fullWidth
              label="Gender"
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              margin="normal"
              required
              error={!!errors.gender}
              helperText={errors.gender}
            >
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>

            <TextField
              fullWidth
              label="Email address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              margin="normal"
              required
              error={!!errors.email}
              helperText={errors.email}
            />

            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              margin="normal"
              required
              error={!!errors.password}
              helperText={errors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirm Password"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={formData.confirmPassword}
              onChange={handleChange}
              margin="normal"
              required
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </>
        );

      case 1:
        return (
          <>
            <TextField
              fullWidth
              label="Business Name"
              name="businessName"
              value={formData.businessName}
              onChange={handleChange}
              margin="normal"
              required
              error={!!errors.businessName}
              helperText={errors.businessName}
            />

            <TextField
              select
              fullWidth
              label="Business Type"
              name="businessType"
              value={formData.businessType}
              onChange={handleChange}
              margin="normal"
              required
              error={!!errors.businessType}
              helperText={errors.businessType}
            >
              <MenuItem value="proprietorship">Proprietorship</MenuItem>
              <MenuItem value="partnership">Partnership</MenuItem>
              <MenuItem value="llp">LLP</MenuItem>
              <MenuItem value="private_limited">Private Limited</MenuItem>
              <MenuItem value="public_limited">Public Limited</MenuItem>
            </TextField>

            <TextField
              fullWidth
              label="Business Registration Number"
              name="businessRegistrationNumber"
              value={formData.businessRegistrationNumber}
              onChange={handleChange}
              margin="normal"
              required
              error={!!errors.businessRegistrationNumber}
              helperText={errors.businessRegistrationNumber}
            />

            <TextField
              fullWidth
              label="GST Number"
              name="gstNumber"
              value={formData.gstNumber}
              onChange={handleChange}
              margin="normal"
              required
              error={!!errors.gstNumber}
              helperText={errors.gstNumber}
              placeholder="e.g. 22AAAAA0000A1Z5"
            />

            <TextField
              fullWidth
              label="PAN Number"
              name="panNumber"
              value={formData.panNumber}
              onChange={handleChange}
              margin="normal"
              required
              error={!!errors.panNumber}
              helperText={errors.panNumber}
              placeholder="e.g. ABCDE1234F"
            />
          </>
        );

      case 2:
        return (
          <>
            <TextField
              fullWidth
              label="Address Line 1"
              name="businessAddress.addressLine1"
              value={formData.businessAddress.addressLine1}
              onChange={handleChange}
              margin="normal"
              required
              error={!!errors["businessAddress.addressLine1"]}
              helperText={errors["businessAddress.addressLine1"]}
            />

            <TextField
              fullWidth
              label="Address Line 2 (Optional)"
              name="businessAddress.addressLine2"
              value={formData.businessAddress.addressLine2}
              onChange={handleChange}
              margin="normal"
            />

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1 }}>
              <TextField
                sx={{ flex: 1 }}
                label="City"
                name="businessAddress.city"
                value={formData.businessAddress.city}
                onChange={handleChange}
                required
                margin="normal"
                error={!!errors["businessAddress.city"]}
                helperText={errors["businessAddress.city"]}
              />
              <TextField
                sx={{ flex: 1 }}
                label="State"
                name="businessAddress.state"
                value={formData.businessAddress.state}
                onChange={handleChange}
                required
                margin="normal"
                error={!!errors["businessAddress.state"]}
                helperText={errors["businessAddress.state"]}
              />
            </Box>

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1 }}>
              <TextField
                sx={{ flex: 1 }}
                label="Country"
                name="businessAddress.country"
                value={formData.businessAddress.country}
                onChange={handleChange}
                required
                margin="normal"
                error={!!errors["businessAddress.country"]}
                helperText={errors["businessAddress.country"]}
              />
              <TextField
                sx={{ flex: 1 }}
                label="Pincode"
                name="businessAddress.pincode"
                value={formData.businessAddress.pincode}
                onChange={handleChange}
                required
                margin="normal"
                error={!!errors["businessAddress.pincode"]}
                helperText={errors["businessAddress.pincode"]}
              />
            </Box>
          </>
        );

      default:
        return "Unknown step";
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        backgroundImage: "url('./img/si2.png')",
        backgroundSize: "cover",
        // backgroundPosition: "center",
        backgroundAttachment: "fixed",
        backgroundRepeat: "no-repeat",
        p: { xs: 3, sm: 4, md: 6 },
        pt: { xs: 4, sm: 6 },
      }}
    >
      <Box
        sx={{
          width: { xs: "100%", sm: 480 },
          maxWidth: 520,
          bgcolor: "white",
          borderRadius: 3,
          boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
          p: { xs: 3, sm: 5 },
          ml: { xs: 0, sm: 2 },
        }}
      >
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Box
            component="img"
            src="/img/tvfmlogo2.png"
            alt="TVFM Logo"
            sx={{ height: 55, width: "auto", mb: 3 }}
          />
          <Typography variant="h4" fontWeight="bold">
            {showOtp ? "Verify Your Email" : "Create Business Account"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {showOtp
              ? `Code sent to ${formData.email}`
              : "Complete registration in 3 easy steps"}
          </Typography>
        </Box>

        {!showOtp ? (
          <>
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel
                  sx={{
                    "& .MuiStepIcon-root": {
                      color: "#e0e0e0", 
                    },
                    "& .MuiStepIcon-root.Mui-active": {
                      color: "black", 
                    }
                    }}>
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            <Box component="form" noValidate>
              {getStepContent(activeStep)}
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 5 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                variant="outlined"
                sx={{
                  color: "#000",
                  borderColor: "#000",
                  "&:hover": { borderColor: "#222" },
                  minWidth: 100,
                }}
              >
                Back
              </Button>

              <Button
                variant="contained"
                onClick={handleNext}
                sx={{
                  minWidth: 120,
                  backgroundColor: "#000",
                  "&:hover": { backgroundColor: "#222" },
                }}
              >
                {activeStep === steps.length - 1 ? "Create Account" : "Next"}
              </Button>
            </Box>
          </>
        ) : (
          <>
            <TextField
              fullWidth
              label="Enter 6-digit OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              variant="outlined"
              margin="normal"
              inputProps={{ maxLength: 6 }}
              autoFocus
            />

            <Button
              fullWidth
              variant="contained"
              onClick={handleVerifyOtp}
              sx={{
                mt: 4,
                py: 1.5,
                backgroundColor: "#000",
                "&:hover": { backgroundColor: "#222" },
              }}
            >
              Verify OTP
            </Button>
          </>
        )}

        <Typography
          variant="body2"
          align="center"
          sx={{ mt: 4, color: "text.secondary" }}
        >
          Already have an account?{" "}
          <Link href="/signin" underline="hover">
            Sign in
          </Link>
        </Typography>
      </Box>
    </Box>
  );
}

export default Signup;